import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Euler, Group, type Mesh, type Points } from 'three';
import {
  GINGIVA,
  type Gingiva,
  OPTIONAL_ITEMS,
  type OptionalItem,
} from '@witmetrics/unison-core';
import { degToRad, isNumber } from '@witmetrics/utils/numbers';
import { findByKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import type { DeltafaceConfig } from '@/utils/scans/deltafaceLoader/types';
import {
  POSITION_PRESET,
  type PositionPreset,
} from '@/utils/scans/trackballControls';
import { useScene } from '@/providers/SceneProvider';
import { getToothGingivaType } from '@/utils/scans/deltafaceLoader';
import { useIds } from '@/hooks/useIds';
import { useToggle } from '@/hooks/useToggle';
import { useMountEffect } from '@/hooks/useMountEffect';
import Scene from '@/components/Scene';
import StepTitle from './StepTitle';
import ArchToggle from './ArchToggle';
import OptionsToggle from './OptionsToggle';
import { buildCompareTeeth, buildMeshGroup, classes, getHotkey } from './utils';

const { MANDIBLE, MAXILLA } = GINGIVA;
const { COMPARE, IPR, ATTACHMENTS } = OPTIONAL_ITEMS;

export type DeltafaceStepViewerProps = {
  className?: string;
  step: number;
  initialConfig: DeltafaceConfig;
};

export default function DeltafaceStepViewer({
  className,
  step,
  initialConfig,
}: DeltafaceStepViewerProps) {
  const activeStep = useRef(step);
  const config = useRef(initialConfig);
  const meshGroup = useRef(new Group());
  const compareGroup = useRef(new Group());
  const initialRotation = useRef(new Euler());
  const { addToScene, zoomToObjects } = useScene();
  const [visibleArches, setArches, { toggleId: toggleArch }] = useIds<Gingiva>([
    MANDIBLE,
    MAXILLA,
  ]);
  const [showCompare, toggleCompare] = useToggle();
  const [showIpr, toggleIpr] = useToggle();
  const [showAttachments, toggleAttachments] = useToggle();

  useMountEffect(() => {
    window.addEventListener('keyup', handleKeyPress);
    return () => {
      window.removeEventListener('keyup', handleKeyPress);
    };
  });

  useEffect(() => {
    config.current = initialConfig;
    const { gingivas } = config.current;
    const teethMeshes = Array.from(config.current.teeth.values()) as Mesh[];
    const iprMeshes = Array.from(
      config.current.iprPointsScene.values()
    ) as Points[];
    const attachmentsMeshes = Array.from(
      config.current.optionalMeshes.values()
    );
    meshGroup.current = buildMeshGroup([
      ...teethMeshes,
      ...iprMeshes,
      ...attachmentsMeshes,
    ]);
    compareGroup.current = buildMeshGroup(buildCompareTeeth(teethMeshes));
    compareGroup.current.visible = false;
    initialRotation.current = meshGroup.current.rotation.clone();
    if (gingivas.get(MANDIBLE)) meshGroup.current.add(gingivas.get(MANDIBLE)!);
    if (gingivas.get(MAXILLA)) meshGroup.current.add(gingivas.get(MAXILLA)!);
    addToScene([meshGroup.current, compareGroup.current], true);
    zoomToObjects([meshGroup.current, compareGroup.current]);
  }, [initialConfig]);

  useEffect(() => {
    if (activeStep.current === step) return;
    activeStep.current = step;
    updateScene();
  }, [step]);

  useEffect(() => {
    config.current.isMandibleVisible = visibleArches.includes(MANDIBLE);
    config.current.isMaxillaVisible = visibleArches.includes(MAXILLA);
    updateScene();
  }, [visibleArches]);

  useEffect(() => {
    if (compareGroup.current) compareGroup.current.visible = showCompare;
  }, [showCompare]);

  useEffect(() => {
    config.current.isIprVisible = showIpr;
    updateScene();
  }, [showIpr]);

  useEffect(() => {
    config.current.isAttachmentsVisible = showAttachments;
    updateScene();
  }, [showAttachments]);

  const handleKeyPress = (e: KeyboardEvent) => {
    const hotkey = getHotkey(e);
    if (!hotkey) return;
    e.preventDefault();
    e.stopPropagation();
    if (hotkey === 'u') toggleArch(MAXILLA);
    else if (hotkey === 'l') toggleArch(MANDIBLE);
    else if (hotkey === 'c') toggleCompare();
    else if (hotkey === 'i') toggleIpr();
    else if (hotkey === 'a') toggleAttachments();
  };

  const updateScene = () => {
    if (!config.current) return console.warn('Scene config not found');
    updateTeethFrames();
    updateOptionalMeshes();
    updateAttachments();
    updateGingivas();
    updateIprs();
  };

  const handleReset = () => {
    meshGroup.current.rotation.copy(initialRotation.current);
    compareGroup.current.rotation.copy(initialRotation.current);
    setArches([MANDIBLE, MAXILLA]);
  };

  const handleRotate = (preset: PositionPreset) => {
    // Front view = initial rotation
    const rotation = initialRotation.current.clone();
    if (preset === POSITION_PRESET.TOP) {
      rotation.x += degToRad(-90);
      setArches([MAXILLA]);
    }
    if (preset === POSITION_PRESET.BOTTOM) {
      rotation.x += degToRad(90);
      setArches([MANDIBLE]);
    }
    if (preset === POSITION_PRESET.LEFT) rotation.z += degToRad(90);
    if (preset === POSITION_PRESET.RIGHT) rotation.z += degToRad(-90);
    if (preset === POSITION_PRESET.BACK) rotation.z += degToRad(180);
    meshGroup.current.rotation.copy(rotation);
    compareGroup.current.rotation.copy(rotation);
  };

  const updateOptionalMeshes = () => {
    if (isEmpty(Object.keys(config.current.attachments))) {
      config.current.optionalMeshesVisible.forEach((value, key) => {
        let toothNumber = config.current.optionalMeshesToothNumbers.get(key);
        let toothType = config.current.optionalMeshesTypes.get(key);
        if (toothNumber && toothNumber > 0 && toothType === 0) {
          config.current.attachments.set(toothNumber, value);
        }
      });
    }
  };

  const updateIprs = () => {
    const { ipr, iprValues } = config.current;
    if (isEmpty(Object.keys(ipr))) {
      iprValues.forEach((value, key) => ipr.set(key, value));
    }
  };

  const updateTeethFrames = () => {
    const { isMandibleVisible, isMaxillaVisible, isIprVisible } =
      config.current;
    const { teeth, frames, iprPointsScene, visibleIprs } = config.current;
    if (teeth.size === 0 || activeStep.current < 0) return;
    teeth.forEach((tooth, key) => {
      // TODO: Switch this to GINGIVA.MANDIBLE and GINGIVA.MAXILLA
      const gingivaType = getToothGingivaType(key);
      const compareTooth = findByKey(
        compareGroup.current.children,
        'name',
        tooth.name
      );
      if (
        (!isMandibleVisible && gingivaType === 0) ||
        (!isMaxillaVisible && gingivaType === 1)
      ) {
        tooth.visible = false;
        if (compareTooth) compareTooth.visible = false;
        if (iprPointsScene.has(key)) iprPointsScene.get(key)!.visible = false;
        return;
      }
      tooth.visible = true;
      if (compareTooth) compareTooth.visible = true;
      if (frames.has(key)) {
        const framesTooth = frames.get(key);
        const singleFrame = framesTooth![activeStep.current];
        tooth.matrix.copy(singleFrame);
        tooth.matrixAutoUpdate = false;
        if (iprPointsScene.has(key)) {
          const points = iprPointsScene.get(key)!;
          const visibleIpr = visibleIprs.get(key);
          points.matrix.copy(singleFrame);
          points.matrixAutoUpdate = false;
          if (!visibleIpr) return;
          if (visibleIpr[activeStep.current] === 0 || !isIprVisible) {
            points.visible = false;
          } else if (visibleIpr[activeStep.current] === 1) {
            points.visible = true;
          }
        }
      }
    });
  };

  const updateAttachments = () => {
    if (config.current.optionalMeshes.size <= 0 || step < 0) return;
    const { optionalMeshes, optionalMeshesTypes } = config.current;
    const { optionalMeshesToothNumbers, optionalMeshesFrames } = config.current;
    const { optionalMeshesVisible, isAttachmentsVisible } = config.current;
    const { isMandibleVisible, isMaxillaVisible } = config.current;
    optionalMeshes.forEach((value, key) => {
      let type = optionalMeshesTypes.get(key);
      let toothNumber = optionalMeshesToothNumbers.get(key);
      if (!isNumber(toothNumber)) return console.error('toothNumber not found');
      let toothType = getToothGingivaType(toothNumber);
      if (
        (!isMandibleVisible &&
          (toothType === 0 || toothNumber === -2 || type === 6)) ||
        (!isMaxillaVisible &&
          (toothType === 1 || toothNumber === -1 || type === 7))
      ) {
        value.visible = false;
        return;
      }
      // TODO: Can we really assume that optionalMeshesVisible.get(key) is not undefined?
      let currentFrame = optionalMeshesFrames.get(key)![step];
      value.matrix.copy(currentFrame);
      value.matrixAutoUpdate = false;
      if (type === 1) {
        const numberMesh = config.current.numbers.get(step);
        if (numberMesh) value.geometry = numberMesh.geometry;
      } else if (type === 6) {
        const wire = config.current.wiresMandible.get(step);
        if (wire) value.geometry = wire.geometry;
      } else if (type === 7) {
        const wire = config.current.wiresMaxilla.get(step);
        if (wire) value.geometry = wire.geometry;
      }
      // 5 --> attachment
      value.visible = isAttachmentsVisible && value.name.includes('5_hdl');
      // eslint-disable-next-line witmetrics/no-multiline-comments
      /* value.visible = !(
      //   type === 4 ||
      //   type === 5 ||
      //   (type === 0 &&
      //     (!isAttachmentsVisible ||
      //       (optionalMeshesVisible.get(key) &&
      //         optionalMeshesVisible.get(key)![step] === 0)))
      // );
       */
    });
  };

  const updateGingivas = () => {
    const { isMaxillaVisible, isMandibleVisible } = config.current;
    const { gingivas, posStartGingivas } = config.current;
    const { mandibleGingivas, maxillaGingivas } = config.current;
    if (gingivas.size <= 1) return;
    [MAXILLA, MANDIBLE].forEach((arch) => {
      const archGingiva = gingivas.get(arch);
      const posStartGingiva = posStartGingivas.get(arch);
      if (!archGingiva) return console.error('archGingiva not found');
      if (!posStartGingiva) return console.error('posStartGingiva not found');
      const positions = archGingiva.geometry.attributes.position.array;
      if (
        (!isMandibleVisible && arch === MANDIBLE) ||
        (!isMaxillaVisible && arch === MAXILLA)
      ) {
        return (archGingiva.visible = false); // No need to continue if hidden
      }
      archGingiva.visible = true;

      for (let j = 0; j < posStartGingiva.length; j++) {
        positions[j] = posStartGingiva[j];
      }
      //Set at step i if step != 0
      if (step > 0) {
        let gingivaPos;
        if (arch === MANDIBLE) gingivaPos = mandibleGingivas.get(step);
        else gingivaPos = maxillaGingivas.get(step);
        if (!gingivaPos) return console.error('gingivaPos not found');
        gingivaPos.forEach(({ x, y, z }, key) => {
          const indexStart = key * 3;
          positions[indexStart] = x;
          positions[indexStart + 1] = y;
          positions[indexStart + 2] = z;
        });
      }
      archGingiva.geometry.attributes.position.needsUpdate = true;
    });
  };

  const handleOptionsChange = (update: OptionalItem[]) => {
    toggleCompare(update.includes(COMPARE));
    toggleIpr(update.includes(IPR));
    toggleAttachments(update.includes(ATTACHMENTS));
  };

  return (
    <div className={twMerge(classes.wrapper, className)}>
      <div className={classes.header}>
        <StepTitle step={step} />
        <ArchToggle visibleArches={visibleArches} onChange={setArches} />
        <OptionsToggle
          showCompare={showCompare}
          showIpr={showIpr}
          showAttachments={showAttachments}
          onChange={handleOptionsChange}
        />
      </div>
      <Scene
        className={classes.scene}
        onReset={handleReset}
        onRotate={handleRotate}
      />
    </div>
  );
}
